import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';
import Logo from '../components/Logo';
import LandingHeroBackground from '../components/LandingHeroBackground';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { redirectAfterAuth } from '../utils/profileSession';

const API = getApiBaseUrl();

export default function LoginPage() {
    const { t } = useTranslation(['auth', 'demo']);
    const [mode, setMode] = useState('password');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [phone, setPhone] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [otpChannel, setOtpChannel] = useState(null);
    const [otpTimer, setOtpTimer] = useState(0);
    const [smsFallbackSending, setSmsFallbackSending] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const otpMaxLength = otpChannel === 'email' ? 6 : 4;

    function getOtpPlaceholder() {
        if (otpChannel === 'call') return t('callOtpCodePlaceholder');
        if (otpChannel === 'sms') return t('smsOtpPlaceholder');
        if (otpChannel === 'email') return t('emailOtpPlaceholder');
        return t('callOtpCodePlaceholder');
    }

    useEffect(() => {
        if (otpTimer <= 0) return;
        const timerId = setInterval(() => setOtpTimer(s => Math.max(0, s - 1)), 1000);
        return () => clearInterval(timerId);
    }, [otpTimer]);

    async function handleLogin(e) {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const login = email.trim();
            const res = await axios.post(`${API}/auth/login`, { email: login, login, password });
            const ok = redirectAfterAuth(res.data);
            if (!ok) {
                setError(t('errors.loginFailed'));
            }
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.error;
            setError(msg || t('errors.loginFailed'));
        }
        setLoading(false);
    }

    async function requestOtp() {
        setLoading(true);
        setError('');
        try {
            const res = await axios.post(`${API}/auth/request-otp`, { phone });
            const channel = res.data?.channel || 'sms';
            setOtpChannel(channel);
            setOtpSent(true);
            setOtpTimer(channel === 'call'
                ? (res.data?.fallbackSmsAvailableAfterSeconds ?? 45)
                : 60);
        } catch (err) {
            const d = err.response?.data;
            setError(d?.message || t('errors.otpSendFailed'));
        }
        setLoading(false);
    }

    async function requestSmsFallback() {
        setSmsFallbackSending(true);
        setError('');
        try {
            await axios.post(`${API}/auth/request-otp-sms-fallback`, { phone });
            setOtpChannel('sms');
            setOtpTimer(60);
        } catch (err) {
            const d = err.response?.data;
            setError(d?.message || t('errors.otpSendFailed'));
        }
        setSmsFallbackSending(false);
    }

    async function loginOtp(e) {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const res = await axios.post(`${API}/auth/login-otp`, { phone, code: otpCode });
            redirectAfterAuth(res.data);
        } catch (err) {
            setError(err.response?.data?.message || t('errors.otpInvalid'));
        }
        setLoading(false);
    }

    const tabStyle = active => ({
        flex: 1, padding: 10, border: 'none', borderRadius: 8, cursor: 'pointer',
        fontWeight: 600, background: active ? 'var(--sp-primary)' : '#f1f5f9',
        color: active ? '#fff' : '#64748b',
        minHeight: 44
    });

    return (
        <div className="sp-auth-page page-login">
            <div className="sp-auth-hero hide-mobile-decor">
                <LandingHeroBackground variant="auth" />
                <div className="sp-auth-hero__content">
                    <Link to="/" style={{ display: 'inline-block', marginBottom: 32 }}>
                        <Logo variant="dark" height={48} />
                    </Link>
                    <p className="sp-auth-hero__tagline">{t('tagline')}</p>
                </div>
            </div>

            <div className="sp-auth-form-panel">
                <div style={{ width: '100%', maxWidth: 420 }}>
                    <div style={{ textAlign: 'center', marginBottom: 24 }} className="hide-desktop-decor">
                        <Link to="/" style={{ display: 'inline-block', margin: '0 auto 24px' }}>
                            <Logo variant="light" height={40} />
                        </Link>
                    </div>

                    <div className="sp-data-card" style={{ padding: 36 }}>
                        <div className="auth-tabs form-row" style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
                            <button type="button" style={tabStyle(mode === 'password')} onClick={() => setMode('password')}>
                                {t('tabPassword')}
                            </button>
                            <button type="button" style={tabStyle(mode === 'sms')} onClick={() => setMode('sms')}>
                                {t('tabSms')}
                            </button>
                        </div>

                        {mode === 'password' ? (
                            <form onSubmit={handleLogin}>
                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                                        {t('emailOrPhone')}
                                    </label>
                                    <input
                                        className="sp-input"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        placeholder={t('emailPlaceholder')}
                                        autoComplete="username"
                                        required
                                    />
                                </div>
                                <div style={{ marginBottom: '24px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                        <label style={{ fontSize: '13px', fontWeight: '600' }}>{t('password')}</label>
                                        <Link
                                            to="/forgot-password"
                                            className="nav-touch"
                                            style={{ fontSize: 12, color: '#EB3333', fontWeight: 600, textDecoration: 'none' }}
                                        >
                                            {t('forgotPassword')}
                                        </Link>
                                    </div>
                                    <input
                                        className="sp-input"
                                        type="password"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        autoComplete="current-password"
                                        required
                                    />
                                </div>
                                <div style={{ marginBottom: '20px' }}>
                                    <LanguageSwitcher variant="inline" />
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="sp-btn sp-btn--primary form-submit"
                                    style={{ width: '100%' }}
                                >
                                    {loading ? t('loggingIn') : `${t('login')} →`}
                                </button>
                            </form>
                        ) : (
                            <form onSubmit={loginOtp}>
                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                                        {t('phone')}
                                    </label>
                                    <input
                                        className="sp-input"
                                        value={phone}
                                        onChange={e => setPhone(e.target.value)}
                                        placeholder="+7 (___) ___-__-__"
                                        autoComplete="tel"
                                        required
                                    />
                                </div>
                                <div style={{ marginBottom: '20px' }}>
                                    <LanguageSwitcher variant="inline" />
                                </div>
                                {!otpSent ? (
                                    <button
                                        type="button"
                                        disabled={loading}
                                        onClick={requestOtp}
                                        className="sp-btn sp-btn--primary form-submit"
                                        style={{ width: '100%' }}
                                    >
                                        {t('sendCode')}
                                    </button>
                                ) : (
                                    <>
                                        {otpChannel === 'call' ? (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    gap: 12,
                                                    alignItems: 'flex-start',
                                                    padding: '12px 14px',
                                                    marginBottom: 16,
                                                    borderRadius: 10,
                                                    background: '#eff6ff',
                                                    border: '1px solid #bfdbfe',
                                                }}
                                            >
                                                <div style={{ fontSize: 22, lineHeight: 1 }}>📞</div>
                                                <div style={{ flex: 1 }}>
                                                    <strong style={{ display: 'block', marginBottom: 4, color: '#1e3a5f' }}>
                                                        {t('callOtpTitle')}
                                                    </strong>
                                                    <p style={{ margin: 0, fontSize: 12, color: '#475569', lineHeight: 1.5 }}>
                                                        {t('callOtpDescription')}
                                                    </p>
                                                </div>
                                            </div>
                                        ) : otpChannel === 'sms' ? (
                                            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12, lineHeight: 1.5 }}>
                                                {t('smsOtpSentHint')}
                                            </p>
                                        ) : null}
                                        <div style={{ marginBottom: '16px' }}>
                                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                                                {otpChannel === 'call' ? t('callOtpCodePlaceholder') : t('otpLabel')}
                                            </label>
                                            <input
                                                className="sp-input"
                                                value={otpCode}
                                                onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, otpMaxLength))}
                                                maxLength={otpMaxLength}
                                                placeholder={getOtpPlaceholder()}
                                                inputMode="numeric"
                                                required
                                                style={{ letterSpacing: 8, fontSize: 20 }}
                                            />
                                        </div>
                                        <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                                            {otpTimer > 0
                                                ? t('resendIn', { sec: String(otpTimer).padStart(2, '0') })
                                                : t('resendReady')}
                                        </p>
                                        {otpTimer === 0 && otpChannel === 'call' && (
                                            <button
                                                type="button"
                                                onClick={requestSmsFallback}
                                                disabled={smsFallbackSending}
                                                className="nav-touch"
                                                style={{ marginBottom: 12, background: 'none', border: 'none', color: '#EB3333', cursor: 'pointer', fontWeight: 600 }}
                                            >
                                                {t('requestSmsFallback')}
                                            </button>
                                        )}
                                        {otpTimer === 0 && otpChannel !== 'call' && (
                                            <button
                                                type="button"
                                                onClick={requestOtp}
                                                className="nav-touch"
                                                style={{ marginBottom: 12, background: 'none', border: 'none', color: '#EB3333', cursor: 'pointer', fontWeight: 600 }}
                                            >
                                                {t('resend')}
                                            </button>
                                        )}
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="sp-btn sp-btn--primary form-submit"
                                            style={{ width: '100%' }}
                                        >
                                            {loading ? t('loggingIn') : t('login')}
                                        </button>
                                    </>
                                )}
                            </form>
                        )}

                        {error && (
                            <div style={{ background: '#fce4ec', color: '#c62828', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginTop: 16 }}>
                                {error}
                            </div>
                        )}

                        <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '14px', color: '#888' }}>
                            {t('auth:noAccount')}{' '}
                            <Link to="/register" className="nav-touch" style={{ color: '#EB3333', fontWeight: '600' }}>
                                {t('auth:register')}
                            </Link>
                        </div>

                        <div style={{ textAlign: 'center', marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--sp-gray-100)' }}>
                            <Link
                                to="/demo-login"
                                className="nav-touch sp-btn sp-btn--secondary"
                                style={{ display: 'inline-flex', width: '100%', justifyContent: 'center', textDecoration: 'none' }}
                            >
                                {t('demo:tryDemo')}
                            </Link>
                            <p style={{ margin: '10px 0 0', fontSize: 12, color: '#94a3b8' }}>{t('demo:noCreditCard')}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
